/**
 * AlertsTab - Low stock email/SMS alerts configuration
 * Configure recipients and trigger alerts for low stock items
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Bell, Mail, Phone, AlertTriangle, Send, Settings, 
  Plus, X, Save, Clock, CheckCircle, XCircle, RefreshCw,
  Package, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/utils/apiClient';

export default function AlertsTab() {
  const [alertStatus, setAlertStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Settings form state
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [smsRecipients, setSmsRecipients] = useState([]);
  const [alertOnLowStock, setAlertOnLowStock] = useState(true);
  const [alertOnOutOfStock, setAlertOnOutOfStock] = useState(true);
  
  // Input states
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  
  // Test modal
  const [showTestModal, setShowTestModal] = useState(false);
  const [testType, setTestType] = useState('email');
  const [testRecipient, setTestRecipient] = useState('');
  const [testSending, setTestSending] = useState(false);

  const fetchAlertStatus = useCallback(async () => {
    try {
      const res = await api.get('/admin/alerts/status');
      const data = res.data;
      setAlertStatus(data);
      
      // Populate settings
      const settings = data.settings || {};
      setEmailEnabled(settings.email_enabled !== false);
      setSmsEnabled(settings.sms_enabled === true);
      setEmailRecipients(settings.email_recipients || []);
      setSmsRecipients(settings.sms_recipients || []);
      setAlertOnLowStock(settings.alert_on_low_stock !== false);
      setAlertOnOutOfStock(settings.alert_on_out_of_stock !== false);
    } catch (err) {
      console.error('Failed to fetch alert status:', err);
      toast.error('Failed to load alert settings');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAlertStatus();
  }, [fetchAlertStatus]);

  // Add email recipient
  const addEmailRecipient = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (emailRecipients.includes(newEmail)) {
      toast.error('Email already added');
      return;
    }
    setEmailRecipients([...emailRecipients, newEmail]);
    setNewEmail('');
  };

  // Remove email recipient
  const removeEmailRecipient = (email) => {
    setEmailRecipients(emailRecipients.filter(e => e !== email));
  };

  // Add SMS recipient
  const addSmsRecipient = () => {
    if (!newPhone || newPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    // Format phone number
    const formatted = newPhone.startsWith('+') ? newPhone : `+${newPhone}`;
    if (smsRecipients.includes(formatted)) {
      toast.error('Phone number already added');
      return;
    }
    setSmsRecipients([...smsRecipients, formatted]);
    setNewPhone('');
  };

  // Remove SMS recipient
  const removeSmsRecipient = (phone) => {
    setSmsRecipients(smsRecipients.filter(p => p !== phone));
  };

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/alerts/settings', {
        email_enabled: emailEnabled,
        sms_enabled: smsEnabled,
        email_recipients: emailRecipients,
        sms_recipients: smsRecipients,
        alert_on_low_stock: alertOnLowStock,
        alert_on_out_of_stock: alertOnOutOfStock
      });
      
      if (res.status === 200) {
        toast.success('Alert settings saved!');
        fetchAlertStatus();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  // Send alert now
  const sendAlertNow = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/alerts/send-now');
      const data = res.data;
      
      if (data.sent) {
        toast.success('Alert sent successfully!');
        fetchAlertStatus();
      } else {
        toast.info(data.message || 'No items to alert about');
      }
    } catch (err) {
      toast.error('Failed to send alert');
    }
    setSending(false);
  };

  // Send test alert
  const sendTestAlert = async () => {
    if (!testRecipient) {
      toast.error(`Please enter a ${testType === 'email' ? 'email address' : 'phone number'}`);
      return;
    }
    
    setTestSending(true);
    try {
      const endpoint = testType === 'email' 
        ? '/admin/alerts/test-email'
        : '/admin/alerts/test-sms';
      const params = testType === 'email' ? { email: testRecipient } : { phone: testRecipient };
      
      const res = await api.post(endpoint, null, { params });
      const data = res.data;
      
      if (data.result?.status === 'sent') {
        toast.success(`Test ${testType} sent!`);
        setShowTestModal(false);
      } else if (data.result?.status === 'skipped') {
        toast.warning(`Test skipped: ${data.result.reason}`);
      } else {
        toast.error(`Failed: ${data.result?.error || 'Unknown error'}`);
      }
    } catch (err) {
      toast.error('Failed to send test');
    }
    setTestSending(false);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentStatus = alertStatus?.current_status || {};
  const hasAlertItems = currentStatus.out_of_stock_count > 0 || currentStatus.low_stock_count > 0;

  return (
    <div className="space-y-6" data-testid="alerts-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Low Stock Alerts</h2>
          <p className="text-muted-foreground">Get notified when items run low</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchAlertStatus} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button 
            onClick={sendAlertNow} 
            disabled={sending || !hasAlertItems}
            className="gap-2"
            data-testid="send-alert-now"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Sending...' : 'Send Alert Now'}
          </Button>
        </div>
      </div>

      {/* Current Status */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className={`rounded-xl p-4 border ${
          currentStatus.out_of_stock_count > 0 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
            : 'bg-card border-border'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              currentStatus.out_of_stock_count > 0 ? 'bg-red-100 dark:bg-red-900/40' : 'bg-muted'
            }`}>
              <XCircle className={`w-5 h-5 ${
                currentStatus.out_of_stock_count > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
              }`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{currentStatus.out_of_stock_count || 0}</p>
              <p className="text-sm text-muted-foreground">Out of Stock</p>
            </div>
          </div>
        </div>

        <div className={`rounded-xl p-4 border ${
          currentStatus.low_stock_count > 0 
            ? 'bg-yellow-50 border-yellow-200' 
            : 'bg-card border-border'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              currentStatus.low_stock_count > 0 ? 'bg-yellow-100' : 'bg-muted'
            }`}>
              <AlertTriangle className={`w-5 h-5 ${
                currentStatus.low_stock_count > 0 ? 'text-yellow-600' : 'text-muted-foreground'
              }`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{currentStatus.low_stock_count || 0}</p>
              <p className="text-sm text-muted-foreground">Low Stock</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
              <Clock className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Last Alert</p>
              <p className="text-sm text-muted-foreground">
                {formatDate(alertStatus?.settings?.last_alert_sent)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Low Stock Items Preview */}
      {hasAlertItems && (
        <div className="bg-card rounded-xl p-4 border border-border">
          <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Items Needing Attention
          </h3>
          <div className="grid md:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
            {currentStatus.out_of_stock_items?.map(item => (
              <div key={item.item_id} className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
                <span className="text-sm text-foreground">{item.item_name}</span>
                <span className="text-xs font-medium text-red-600 dark:text-red-400">OUT</span>
              </div>
            ))}
            {currentStatus.low_stock_items?.map(item => (
              <div key={item.item_id} className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg">
                <span className="text-sm text-foreground">{item.item_name}</span>
                <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">{item.current_stock} left</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Alert Settings
        </h3>

        <div className="space-y-6">
          {/* Alert Types */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium text-sm text-foreground">Alert on Low Stock</p>
                <p className="text-xs text-muted-foreground">When items fall below threshold</p>
              </div>
              <Switch
                checked={alertOnLowStock}
                onCheckedChange={setAlertOnLowStock}
                data-testid="alert-low-stock-toggle"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium text-sm text-foreground">Alert on Out of Stock</p>
                <p className="text-xs text-muted-foreground">When items reach zero</p>
              </div>
              <Switch
                checked={alertOnOutOfStock}
                onCheckedChange={setAlertOnOutOfStock}
                data-testid="alert-out-of-stock-toggle"
              />
            </div>
          </div>

          {/* Email Settings */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                <span className="font-medium text-foreground">Email Alerts</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={emailEnabled}
                  onCheckedChange={setEmailEnabled}
                  data-testid="email-enabled-toggle"
                />
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => { setTestType('email'); setTestRecipient(''); setShowTestModal(true); }}
                >
                  Test
                </Button>
              </div>
            </div>
            
            {emailEnabled && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Add email address..."
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addEmailRecipient()}
                    className="flex-1"
                    data-testid="email-input"
                  />
                  <Button onClick={addEmailRecipient} variant="outline" size="icon">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {emailRecipients.map(email => (
                    <span 
                      key={email} 
                      className="inline-flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm"
                    >
                      {email}
                      <button onClick={() => removeEmailRecipient(email)} className="hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {emailRecipients.length === 0 && (
                    <span className="text-sm text-muted-foreground">No email recipients added</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* SMS Settings */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-primary" />
                <span className="font-medium text-foreground">SMS Alerts</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={smsEnabled}
                  onCheckedChange={setSmsEnabled}
                  data-testid="sms-enabled-toggle"
                />
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => { setTestType('sms'); setTestRecipient(''); setShowTestModal(true); }}
                >
                  Test
                </Button>
              </div>
            </div>
            
            {smsEnabled && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    placeholder="Add phone number (with country code)..."
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addSmsRecipient()}
                    className="flex-1"
                    data-testid="phone-input"
                  />
                  <Button onClick={addSmsRecipient} variant="outline" size="icon">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {smsRecipients.map(phone => (
                    <span 
                      key={phone} 
                      className="inline-flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm"
                    >
                      {phone}
                      <button onClick={() => removeSmsRecipient(phone)} className="hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {smsRecipients.length === 0 && (
                    <span className="text-sm text-muted-foreground">No SMS recipients added</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-settings">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </div>

      {/* Alert History */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
          <History className="w-5 h-5" />
          Alert History
        </h3>
        <div className="space-y-2">
          {alertStatus?.recent_alerts?.length > 0 ? (
            alertStatus.recent_alerts.map((alert, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bell className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-foreground">
                      Alert sent for {alert.items_count} items
                    </p>
                    <p className="text-xs text-muted-foreground">{formatDate(alert.sent_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {alert.email_result?.status === 'sent' && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Email Sent</span>
                  )}
                  {alert.sms_result?.status === 'sent' && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">SMS Sent</span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-muted-foreground py-4">No alerts sent yet</p>
          )}
        </div>
      </div>

      {/* Test Modal */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Send Test {testType === 'email' ? 'Email' : 'SMS'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{testType === 'email' ? 'Email Address' : 'Phone Number'}</Label>
              <Input
                type={testType === 'email' ? 'email' : 'tel'}
                placeholder={testType === 'email' ? 'test@example.com' : '+1234567890'}
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                data-testid="test-recipient-input"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              A test alert will be sent with sample low stock items.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTestModal(false)}>
                Cancel
              </Button>
              <Button onClick={sendTestAlert} disabled={testSending} data-testid="send-test-btn">
                {testSending ? 'Sending...' : 'Send Test'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
